import { Link } from '@tanstack/react-router'
import { ArrowRight, Lock, Sparkles, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { useMe } from '@/features/auth/api'
import { usePermission } from '@/hooks/use-permission'

/** Full-width "not included in your plan" card (same look as the channels upsell). */
export function PlanUpsell({ title, description, points }: { title: string; description: ReactNode; points: { icon: LucideIcon; text: string }[] }) {
  const { data: me } = useMe()
  return (
    <div className="relative overflow-hidden rounded-2xl border bg-card px-6 py-12 text-center shadow-sm sm:px-12">
      <div className="pointer-events-none absolute -top-24 left-1/2 size-72 -translate-x-1/2 rounded-full bg-brand-gradient opacity-[0.12] blur-3xl" aria-hidden />
      <div className="relative">
        <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-glow">
          <Lock className="size-6" strokeWidth={1.75} />
        </div>
        <p className="mt-5 text-xs font-semibold tracking-[0.12em] text-primary uppercase">Not included in your plan</p>
        <h2 className="mt-2 text-xl font-semibold tracking-tight">{title}</h2>
        <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
          {description}
          {me?.subscription ? ` You’re on the ${me.subscription.plan.name} plan.` : ''}
        </p>
        <ul className="mx-auto mt-6 grid max-w-xl gap-3 text-left text-[13px] sm:grid-cols-3">
          {points.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3">
              <Icon className="mt-0.5 size-4 shrink-0 text-primary" /> {text}
            </li>
          ))}
        </ul>
        <div className="mt-6 flex justify-center">
          <UpgradeAction />
        </div>
      </div>
    </div>
  )
}

/** "Compare plans" for billing managers, otherwise a nudge to ask an owner. */
export function UpgradeAction({ compact = false }: { compact?: boolean }) {
  const canBill = usePermission('billing.manage')
  if (!canBill) return <p className="text-sm text-muted-foreground">{compact ? 'Ask an owner to upgrade.' : 'Ask an organization owner to upgrade the plan.'}</p>
  return (
    <Button variant={compact ? 'link' : 'brand'} size={compact ? 'sm' : 'default'} asChild>
      <Link to="/settings/subscription" search={{ change: true }}>
        {!compact && <Sparkles />} Compare plans <ArrowRight />
      </Link>
    </Button>
  )
}

/** Inline hint next to a control that needs a higher plan. */
export function FeatureHint({ children }: { children: ReactNode }) {
  const canBill = usePermission('billing.manage')
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-muted-foreground">
      <Lock className="size-3 shrink-0" /> {children}{' '}
      {canBill ? (
        <Link to="/settings/subscription" search={{ change: true }} className="font-medium text-primary hover:underline">
          Upgrade
        </Link>
      ) : (
        'Ask an owner to upgrade.'
      )}
    </p>
  )
}
